import { useMemo } from "react";
import { notAfterTodayIssue, rangeIssue } from "@shared/connectors/fieldRules";
import { MASKED_VALUE } from "@shared/types/dataflows";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { logger } from "@/lib/logger";
import type { FieldDefinition, FieldErrors, FieldStateMap, FormValues } from "./types";

/**
 * The connector definition's own validation rules, run against the current
 * values (FR-40).
 *
 * Ported from skypoint-studio's `useValidation.ts`. The server re-checks only
 * the numeric range (`min`, `max`; `shared/connectors/fieldRules.ts`) of a
 * visible field, and the connector hub the sign-in; the required, length and
 * pattern rules hold only here. Hidden fields are never validated, on either
 * side (`isFieldVisible`): a dependency that hides a field also releases its
 * rules, otherwise a form could be unsubmittable for a reason the user cannot
 * see.
 *
 * Both `console.warn` calls became `logger.warn` (GUIDELINES.md §4), and
 * neither logs the pattern or the value — a pattern is connector configuration
 * and a value may be a credential.
 */

/** Longer than this and the pattern is refused unexecuted. */
const MAX_PATTERN_LENGTH = 500;

/**
 * A cheap guard against catastrophic backtracking.
 *
 * The server does not check a pattern (it holds only `min` and `max`); this
 * guard exists because a ReDoS pattern executed in the browser freezes the
 * tab, and a frozen tab is indistinguishable from a broken screen. A refused
 * pattern is skipped: it is a seed problem, not the person's. It catches
 * nested quantifiers —
 * `(a+)+`, `(a*){2,}` — and anything implausibly long.
 */
function isSafeRegex(pattern: string): boolean {
  if (pattern.length > MAX_PATTERN_LENGTH) return false;
  const nestedQuantifier = /\([^)]*[+*}][^)]*\)[+*?{]/;
  return !nestedQuantifier.test(pattern);
}

/** The pattern rule's message when `text` does not match; null when it matches or is skipped. */
function patternIssue(rule: { value: string; message: string }, text: string): string | null {
  const pattern = rule.value;
  if (!isSafeRegex(pattern)) {
    logger.warn("connector form: regex pattern refused as unsafe, rule skipped", {
      "skydata.pattern_length": pattern.length,
    });
    // Skipped, not enforced anywhere else: the server holds only `min` and `max`, and
    // blocking the user on a seed problem would not help them.
    return null;
  }
  try {
    if (!new RegExp(pattern).test(text)) return rule.message;
  } catch (error) {
    logger.warn("connector form: regex pattern did not compile, rule skipped", {
      "skydata.pattern_length": pattern.length,
      "skydata.error_name": error instanceof Error ? error.name : "unknown",
    });
    return null;
  }
  return null;
}

function validateOne(
  field: FieldDefinition,
  value: unknown,
  isRequired: boolean,
  zone: string,
): string | null {
  const { validation } = field;
  const text = value == null ? "" : String(value);

  if (isRequired && text.trim() === "") {
    return validation.required?.message ?? `${field.name} is required.`;
  }

  // Empty and optional: every remaining rule describes a value that is not there.
  if (text.trim() === "") return null;

  if (validation.minLength && text.length < validation.minLength.value) {
    return validation.minLength.message;
  }
  if (validation.maxLength && text.length > validation.maxLength.value) {
    return validation.maxLength.message;
  }
  // The server holds the same rule (`shared/connectors/fieldRules.ts`, round 7).
  const outOfRange = rangeIssue(validation, typeof value === "number" ? value : text);
  if (outOfRange) return outOfRange;

  if (validation.pattern) {
    const mismatch = patternIssue(validation.pattern, text);
    if (mismatch) return mismatch;
  }

  // #1094: "today" is the person's own (their chosen zone, else this device's). The server holds
  // the rule too, on the day furthest ahead, as it has no reader's zone.
  return notAfterTodayIssue(validation.notAfterToday, text, new Date(), zone);
}

export function useValidation(
  fieldDefinitions: FieldDefinition[],
  formValues: FormValues,
  fieldState: FieldStateMap,
): FieldErrors {
  const zone = useDisplayTz();
  return useMemo(() => {
    const errors: FieldErrors = {};

    for (const field of fieldDefinitions) {
      const state = fieldState[field.id];
      if (state && !state.visible) {
        errors[field.id] = null;
        continue;
      }
      // Round 7 (skypoint-studio #930): a saved credential reads back as the sentinel, which
      // means "keep what is stored" (FR-43). It is not the value, so no rule of the value holds
      // it, as on the server. A non-credential field holding five asterisks is still validated.
      if (field.isCredential && formValues[field.id] === MASKED_VALUE) {
        errors[field.id] = null;
        continue;
      }
      const isRequired = state?.required ?? field.validation.required?.value ?? false;
      errors[field.id] = validateOne(field, formValues[field.id], isRequired, zone);
    }

    return errors;
  }, [fieldDefinitions, formValues, fieldState, zone]);
}

/** True when any visible field currently carries an error. */
export function hasFieldErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some((error) => error !== null);
}
